import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Flex } from "theme-ui";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import { themevals } from "theme/themevals";
import { getQueueRecords, dismissQueueRecord, retireRedoQuote, QueueRecords } from "api/Dashboard";
import { useToast } from "components/toast";

/**
 * The records behind a work-queue row.
 *
 * WHY A PAGE AND NOT A FILTER. The integrity rows - purchase orders
 * that disagree with their line items, purchase orders whose items
 * carry no depot - have no natural home on an existing list. Nobody
 * wants a permanent "drifted POs" filter on the PO list, and landing an
 * operator on 631 rows with no indication which four matter is barely
 * better than the dead-end button this replaces.
 *
 * THE LIST COMES FROM THE SAME PREDICATE AS THE COUNT. A drill-down
 * computed a second way is how "4 purchase orders" opens onto three,
 * and after that neither number gets believed.
 *
 * The page leads with WHY, not with the rows. Someone arriving here
 * clicked a one-line summary; the thing they need first is what went
 * wrong and what fixing it looks like.
 */
const QueueDrillDown: React.FC = () => {
  const { rowId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<QueueRecords | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Which record is mid-dismiss. Per-record rather than a page-wide
  // flag so a slow call on one row does not freeze the rest.
  const [dismissing, setDismissing] = useState<string | null>(null);
  const toast = useToast();

  /**
   * Mark one record handled and drop it from the list in place.
   *
   * Removed locally rather than re-fetched: the row is gone from the
   * server's answer now, and a re-fetch would repaint the whole page to
   * show one fewer line. `stopPropagation` because the row navigates.
   */
  const onDismiss = async (e: any, recordId: string) => {
    e.stopPropagation();
    if (!rowId || dismissing) return;
    setDismissing(recordId);
    const res: any = await dismissQueueRecord(rowId, recordId);
    setDismissing(null);
    if (res?.dismissed) {
      setData((prev) =>
        prev
          ? { ...prev, records: prev.records.filter((r) => r.id !== recordId) }
          : prev,
      );
      toast.add("Marked as handled.", "var(--theme-ui-colors-success)");
      return;
    }
    toast.add(
      res?.message || "Could not mark that as handled.",
      "var(--theme-ui-colors-danger)",
    );
  };

  /**
   * Retire the older quote on a possible-redo record. Confirmed first -
   * it cancels POs and voids draft invoices, which Mark handled never
   * does. The server names what it changed so the toast can say which
   * drafts still need voiding in Zoho.
   */
  const onRetire = async (e: any, recordId: string) => {
    e.stopPropagation();
    if (dismissing) return;
    if (
      !window.confirm(
        "Retire the older quote? It is marked superseded, its open purchase orders are cancelled and its draft invoices are voided. Zoho is not changed.",
      )
    ) {
      return;
    }
    setDismissing(recordId);
    const res: any = await retireRedoQuote(recordId);
    setDismissing(null);
    if (res?.retired) {
      setData((prev) =>
        prev ? { ...prev, records: prev.records.filter((r) => r.id !== recordId) } : prev,
      );
      const voided: string[] = res.voidedInvoices ?? [];
      toast.add(
        voided.length ? "Retired. Void " + voided.join(", ") + " in Zoho by hand." : "Retired.",
        "var(--theme-ui-colors-success)",
      );
      return;
    }
    toast.add(res?.message || "Could not retire that quote.", "var(--theme-ui-colors-danger)");
  };

  useEffect(() => {
    let cancelled = false;
    if (!rowId) return;
    getQueueRecords(rowId)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e: any) => {
        if (!cancelled) {
          setError(
            e?.response?.status === 400
              ? "That queue item has no drill-down."
              : "Could not load these records.",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [rowId]);

  useEffect(() => {
    document.title = `${process.env.REACT_APP_NAME} | Needs attention`;
  }, []);

  const money = (n: number) => "$" + Math.round(n).toLocaleString("en-US");

  /**
   * BAND THE RECORDS WHEN THE PAYLOAD ASKS FOR IT.
   *
   * A flat list is right for a five-row integrity queue and wrong for
   * fifty money records: sorted by value it answers "what is biggest"
   * and nothing about what to do next. When records carry a `group`,
   * they are banded by next action with a subtotal per band, because
   * the SUBTOTAL is what someone acts on — "$40k is finished and
   * unbilled" is a morning's work; one row of it is not.
   *
   * Rows without a group fall into a single unlabelled band and render
   * exactly as they did before, so no existing drill-down changes.
   */
  const bands = React.useMemo(() => {
    const records: any[] = data?.records ?? [];
    if (!records.some((r) => r.group)) {
      return [{ group: "", order: 0, records, subtotal: 0 }];
    }
    const byGroup = new Map<string, { group: string; order: number; records: any[]; subtotal: number }>();
    for (const r of records) {
      const key = r.group || "Other";
      // Built and assigned in one step rather than set-then-get, so
      // there is no lookup that needs asserting non-null.
      const band = byGroup.get(key) ?? {
        group: key,
        order: typeof r.groupOrder === "number" ? r.groupOrder : 99,
        records: [] as any[],
        subtotal: 0,
      };
      band.records.push(r);
      band.subtotal += typeof r.amount === "number" ? r.amount : 0;
      byGroup.set(key, band);
    }
    return [...byGroup.values()].sort((a, b) => a.order - b.order);
  }, [data]);

  return (
    <>
      <Header />
      <Page
        title={data?.title || "Needs attention"}
        subtitle="From the dashboard work queue."
      >
        <Box>
          {error && (
            <Box
              sx={{
                padding: "16px",
                borderRadius: "6px",
                border: "1px solid",
                borderColor: themevals.colors.inputOutline,
                backgroundColor: "#FDF3F3",
                fontFamily: "Poppins, sans-serif",
                fontSize: "13px",
                color: themevals.colors.danger,
              }}
            >
              {error}
            </Box>
          )}

          {data && (
            <>
              <Box
                sx={{
                  padding: "14px 16px",
                  borderRadius: "6px",
                  backgroundColor: "#F7F9FC",
                  border: "1px solid",
                  borderColor: themevals.colors.inputOutline,
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "13px",
                  color: themevals.colors.base_800,
                  lineHeight: 1.6,
                  marginBottom: "20px",
                  maxWidth: "760px",
                }}
              >
                {data.explanation}
              </Box>

              {data.records.length === 0 ? (
                <Box
                  sx={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "13px",
                    color: themevals.colors.dkgrey,
                  }}
                >
                  {/* The row disappears from the queue when the condition
                      clears, so arriving at an empty list means someone
                      fixed it between the dashboard load and this click.
                      Say that rather than showing a blank page. */}
                  Nothing matches any more. This was fixed since the dashboard
                  loaded, or the queue row has already cleared.
                </Box>
              ) : (
                <Box>
                  {bands.map((band) => (
                    <Box key={band.group || "all"} sx={{ marginBottom: "20px" }}>
                      {band.group && (
                        <Flex
                          sx={{
                            alignItems: "baseline",
                            gap: "10px",
                            marginBottom: "8px",
                          }}
                        >
                          <Box
                            sx={{
                              fontFamily: "Poppins, sans-serif",
                              fontSize: "13px",
                              fontWeight: 600,
                              color: "base_800",
                            }}
                          >
                            {band.group}
                          </Box>
                          <Box
                            sx={{
                              fontFamily: "Poppins, sans-serif",
                              fontSize: "12px",
                              color: themevals.colors.dkgrey,
                            }}
                          >
                            {band.records.length === 1
                              ? "1 project"
                              : band.records.length + " projects"}
                          </Box>
                          <Box sx={{ flex: 1 }} />
                          {band.subtotal > 0 && (
                            <Box
                              sx={{
                                fontFamily: "Poppins, sans-serif",
                                fontSize: "13px",
                                fontWeight: 600,
                                color: "base_800",
                              }}
                            >
                              {money(band.subtotal)}
                            </Box>
                          )}
                        </Flex>
                      )}

                      <Box
                        sx={{
                          backgroundColor: "#FFFFFF",
                          borderRadius: "6px",
                          border: "1px solid",
                          borderColor: themevals.colors.inputOutline,
                        }}
                      >
                        {band.records.map((record: any) => (
                    <Flex
                      key={record.id}
                      onClick={() => navigate(record.path)}
                      sx={{
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "12px",
                        padding: "12px 16px",
                        cursor: "pointer",
                        borderBottom: "1px solid",
                        borderColor: themevals.colors.inputOutline,
                        ":hover": { backgroundColor: "#F7F9FC" },
                        ":last-of-type": { borderBottom: "none" },
                      }}
                    >
                      <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
                        <Box
                          sx={{
                            fontFamily: "Poppins, sans-serif",
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "base_800",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {record.label}
                        </Box>
                        <Box
                          sx={{
                            fontFamily: "Poppins, sans-serif",
                            fontSize: "12px",
                            // urgent: the date in the line needs action (passed, missing).
                            color: record.urgent ? "#A32D2D" : themevals.colors.dkgrey,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {record.detail}
                        </Box>
                      </Box>
                      <Flex sx={{ alignItems: "center", gap: "14px", flexShrink: 0 }}>
                        {/* Money in its own right-aligned column, so the
                            figures form a scannable edge instead of
                            sitting mid-sentence in the detail line. */}
                        {typeof record.amount === "number" && (
                          <Box
                            sx={{
                              fontFamily: "Poppins, sans-serif",
                              fontSize: "13px",
                              fontWeight: 600,
                              color: "base_800",
                              textAlign: "right",
                            }}
                          >
                            {money(record.amount)}
                          </Box>
                        )}
                        {/* Only for rows whose condition is a past event.
                            A derived row has no Dismiss at all — hiding
                            an overdue invoice is not the same as paying
                            it, and offering the button would invite
                            exactly that. */}
                        {data.retirable && (
                          <button
                            type="button"
                            disabled={dismissing === record.id}
                            onClick={(e: any) => onRetire(e, record.id)}
                            style={{
                              fontFamily: "Poppins, sans-serif",
                              fontSize: "12px",
                              fontWeight: 500,
                              padding: "5px 11px",
                              borderRadius: "4px",
                              background: "transparent",
                              border: `1px solid ${themevals.colors.danger}`,
                              color: themevals.colors.danger,
                              cursor: dismissing === record.id ? "default" : "pointer",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Retire older quote
                          </button>
                        )}
                        {data.dismissable && (
                          // A plain <button>, not `Box as="button"`:
                          // theme-ui's Box props do not include `type` or
                          // `disabled`, and both matter here — `type`
                          // keeps it out of any enclosing form, and
                          // `disabled` is the real guard against a double
                          // click landing two dismisses. Same pattern as
                          // the client Locations rows.
                          <button
                            type="button"
                            disabled={dismissing === record.id}
                            onClick={(e: any) => onDismiss(e, record.id)}
                            style={{
                              fontFamily: "Poppins, sans-serif",
                              fontSize: "12px",
                              fontWeight: 500,
                              padding: "5px 11px",
                              borderRadius: "4px",
                              background: "transparent",
                              border: `1px solid ${themevals.colors.inputOutline}`,
                              color: themevals.colors.dkgrey,
                              cursor:
                                dismissing === record.id ? "default" : "pointer",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {dismissing === record.id
                              ? "Marking\u2026"
                              : data.dismissLabel || "Mark handled"}
                          </button>
                        )}
                        <Box
                          sx={{
                            fontFamily: "Poppins, sans-serif",
                            fontSize: "12px",
                            fontWeight: 500,
                            color: themevals.colors.base_800,
                          }}
                        >
                          Open
                        </Box>
                      </Flex>
                    </Flex>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>
      </Page>
    </>
  );
};

export default QueueDrillDown;
